import {
	MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { useStableImperativeCallback } from "./useStableImperativeCallback";

type UseDoubleClickOptions = {
	onSingleClick: (args: MouseEvent) => void;
	onDoubleClick: (args: MouseEvent) => void;
	maxDelayInMs?: number;
};

type DoubleClickResult = [
	clickHandler: (args: MouseEvent) => void,
	isDoubleClickPending: boolean
];

export function useDoubleClick(options: UseDoubleClickOptions): DoubleClickResult {
	const {
		onSingleClick,
		onDoubleClick,
		maxDelayInMs = 225,
	} = options;

	const [isDoubleClickPending, setIsDoubleClickPending] = useState<boolean>(false);
	const timeoutRef = useRef<SetTimeoutHandle | null>(null);

	const stableOnSingleClick: typeof onSingleClick = useStableImperativeCallback(onSingleClick);
	const stableOnDoubleClick: typeof onDoubleClick = useStableImperativeCallback(onDoubleClick);

	const clickHandler = useCallback(
		(args: MouseEvent): void => {
			if (isDoubleClickPending) {
				setIsDoubleClickPending(false);

				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				stableOnDoubleClick(args);
			} else {
				setIsDoubleClickPending(true);

				timeoutRef.current = setTimeout(
					(): void => {
						setIsDoubleClickPending(false);
						stableOnSingleClick(args);
					},
					maxDelayInMs
				);
			}
		},
		[
			isDoubleClickPending,
			stableOnDoubleClick,
			maxDelayInMs,
			stableOnSingleClick
		]
	);

	useEffect(
		(): () => void=> {
			return () => {
				if ((timeoutRef.current)) {
					clearTimeout(timeoutRef.current);
				}
			};
		},
		[]
	);

	return [clickHandler, isDoubleClickPending];
}
